// src/Admin/AllProducts.jsx
import { useEffect, useState } from "react";

const API_URL = "/api/products/";
const COLORS = ["White", "Black", "Blue", "Red", "Pink", "Green", "Yellow", "Grey", "Navy"];
const SIZES = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];

export default function AllProducts() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);

  // FETCH ALL PRODUCTS
  useEffect(() => {
    fetch(API_URL)
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : data.results || [];
        setProducts(list);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  // DELETE PRODUCT
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product permanently?")) return;
    await fetch(`${API_URL}${id}/`, { method: "DELETE" });
    setProducts(prev => prev.filter(p => p.id !== id));
  };

  // OPEN EDIT MODAL
  const openEdit = (product) => {
    setEditingProduct({
      id: product.id,
      name: product.name || "",
      price: product.price || "",
      offer_price: product.offer_price || "",
      description: product.description || "",
      variants: product.variants?.length > 0 ? product.variants : [{ color: "", size: "", stock: "", image: null }]
    });
  };

  // HANDLE TEXT INPUT
  const handleTextChange = (e) => {
    const { name, value } = e.target;
    setEditingProduct(prev => ({
      ...prev,
      [name]: name.includes("price") ? Number(value) || "" : value
    }));
  };

  // VARIANT HANDLERS
  const updateVariant = (index, field, value) => {
    setEditingProduct(prev => {
      const newVariants = [...prev.variants];
      newVariants[index] = { ...newVariants[index], [field]: value };
      return { ...prev, variants: newVariants };
    });
  };

  const addVariant = () => {
    setEditingProduct(prev => ({
      ...prev,
      variants: [...prev.variants, { color: "", size: "", stock: "", image: null }]
    }));
  };

  const removeVariant = (index) => {
    if (editingProduct.variants.length === 1) {
      alert("At least one variant is required");
      return;
    }
    setEditingProduct(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  const handleImageChange = (index, file) => {
    if (!file) return;
    setEditingProduct(prev => {
      const newVariants = [...prev.variants];
      newVariants[index].image = file;
      newVariants[index].preview = URL.createObjectURL(file);
      return { ...prev, variants: newVariants };
    });
  };

  // SAVE PRODUCT
  const handleSave = async () => {
    if (!editingProduct.name || !editingProduct.price) {
      alert("Name and Price are required");
      return;
    }

    setSaving(true);
    const fd = new FormData();
    fd.append("name", editingProduct.name);
    fd.append("price", editingProduct.price);
    if (editingProduct.offer_price) fd.append("offer_price", editingProduct.offer_price);
    if (editingProduct.description) fd.append("description", editingProduct.description);

    // Clear old variants & add new ones
    editingProduct.variants.forEach((v, i) => {
      if (v.color && v.stock >= 0) {
        fd.append(`variants[${i}]color`, v.color);
        if (v.size) fd.append(`variants[${i}]size`, v.size);
        fd.append(`variants[${i}]stock`, v.stock);
        if (v.image instanceof File) {
          fd.append(`variant_image_${i}`, v.image);
        }
      }
    });

    try {
      const res = await fetch(`${API_URL}${editingProduct.id}/`, {
        method: "PATCH",
        body: fd,
      });

      if (!res.ok) throw await res.json();
      const updated = await res.json();

      setProducts(prev => prev.map(p => p.id === updated.id ? updated : p));
      setEditingProduct(null);
      alert("Product updated successfully!");
    } catch (err) {
      console.error(err);
      alert("Update failed: " + JSON.stringify(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 font-['Roboto']">
      <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-lg p-8">

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <h1 className="text-3xl font-medium text-gray-900">All Products</h1>
          <div className="flex gap-3 w-full sm:w-auto">
            <input
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-xl w-full sm:w-80 focus:outline-none focus:ring-2 focus:ring-black"
            />
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-black text-white rounded-xl hover:bg-gray-800 transition"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Loading / Table */}
        {loading ? (
          <p className="text-center py-20 text-gray-500">Loading products...</p>
        ) : filtered.length === 0 ? (
          <p className="text-center py-20 text-gray-500 text-lg">No products found</p>
        ) : (
          <div className="overflow-x-auto border rounded-xl">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                <tr>
                  <th className="px-6 py-4 text-left">ID</th>
                  <th className="px-6 py-4 text-left">Product</th>
                  <th className="px-6 py-4 text-left">Price</th>
                  <th className="px-6 py-4 text-left">Offer</th>
                  <th className="px-6 py-4 text-left">Total Stock</th>
                  <th className="px-6 py-4 text-left">Variants</th>
                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filtered.map(p => {
                  const totalStock = p.variants?.reduce((a, v) => a + (v.stock || 0), 0) || 0;
                  const variantCount = p.variants?.length || 0;

                  return (
                    <tr key={p.id} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4 font-medium">#{p.id}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {p.variants?.[0]?.image_url ? (
                            <img src={p.variants[0].image_url} alt="" className="w-12 h-12 object-cover rounded-lg" />
                          ) : (
                            <div className="w-12 h-12 bg-gray-200 rounded-lg" />
                          )}
                          <span className="font-medium text-gray-900">{p.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">₹{p.price}</td>
                      <td className="px-6 py-4 text-green-600 font-medium">
                        {p.offer_price ? `₹${p.offer_price}` : "—"}
                      </td>
                      <td className="px-6 py-4 font-bold">{totalStock}</td>
                      <td className="px-6 py-4 text-gray-600">
                        {variantCount} variant{variantCount !== 1 && "s"}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => openEdit(p)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition mr-2"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(p.id)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* EDIT MODAL */}
      {editingProduct && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-screen overflow-y-auto p-8">
            <h2 className="text-2xl font-medium mb-6">Edit Product</h2>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <input
                name="name"
                value={editingProduct.name}
                onChange={handleTextChange}
                placeholder="Product Name"
                className="px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black"
              />
              <input
                name="price"
                type="number"
                value={editingProduct.price}
                onChange={handleTextChange}
                placeholder="Regular Price"
                className="px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black"
              />
              <input
                name="offer_price"
                type="number"
                value={editingProduct.offer_price}
                onChange={handleTextChange}
                placeholder="Offer Price (optional)"
                className="px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black"
              />
              <textarea
                name="description"
                value={editingProduct.description}
                onChange={handleTextChange}
                placeholder="Description (optional)"
                rows="3"
                className="px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black md:col-span-2"
              />
            </div>

            <h3 className="text-xl font-medium mb-4">Variants</h3>
            <div className="space-y-6 mb-8">
              {editingProduct.variants.map((variant, i) => (
                <div key={i} className="p-6 bg-gray-50 rounded-xl border">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                    <select
                      value={variant.color}
                      onChange={(e) => updateVariant(i, "color", e.target.value)}
                      className="px-4 py-3 border rounded-xl"
                    >
                      <option value="">Select Color</option>
                      {COLORS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input
                      type="text"
                      value={variant.size || ""}
                      onChange={(e) => updateVariant(i, "size", e.target.value.toUpperCase())}
                      placeholder="Size (e.g. M, XL)"
                      className="px-4 py-3 border rounded-xl"
                    />
                    <input
                      type="number"
                      value={variant.stock || ""}
                      onChange={(e) => updateVariant(i, "stock", e.target.value)}
                      placeholder="Stock"
                      className="px-4 py-3 border rounded-xl"
                    />
                  </div>

                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageChange(i, e.target.files[0])}
                      className="text-sm"
                    />
                    {variant.preview || variant.image_url ? (
                      <img
                        src={variant.preview || variant.image_url}
                        alt="preview"
                        className="w-24 h-24 object-cover rounded-lg border"
                      />
                    ) : (
                      <div className="w-24 h-24 bg-gray-200 rounded-lg border-2 border-dashed" />
                    )}
                    {editingProduct.variants.length > 1 && (
                      <button
                        onClick={() => removeVariant(i)}
                        className="ml-auto px-4 py-2 bg-red-600 text-white rounded-lg text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={addVariant}
              className="mb-6 px-6 py-3 bg-gray-200 rounded-xl hover:bg-gray-300 transition"
            >
              + Add Another Variant
            </button>

            <div className="flex justify-end gap-4">
              <button
                onClick={() => setEditingProduct(null)}
                className="px-8 py-3 bg-gray-200 rounded-xl hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-8 py-3 bg-black text-white rounded-xl hover:bg-gray-800 disabled:opacity-50 transition"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}